import { FullWidthPage, useMediaQuery, useTheme } from "@wayflyer/flyui";
import type { ReferencedMultiChartDataPoint, ValueType } from "@wayflyer/flyui-dataviz";
import {
  ChartFilterSwitches,
  DatePickerHeader,
  PanelContainer,
  PanelPadding,
  ReferencedMultiLineBarChart,
  TemporalResolutionPickers,
} from "@wayflyer/flyui-dataviz";

import { ChartTopRow, DataGroupContainer } from "../../components/styled";
import type { SummaryPanelItemProps } from "../../components/summary-panel";
import { SummaryPanel } from "../../components/summary-panel";
import { MetricTypes } from "../../autogen/metrics";

import type { TikTokAccountData } from "./tiktok-spend";
import { getGroupNameWithId } from "./tiktok-spend-datapoints";

export interface InternalTikTokSpendScreenProps {
  pageTitle: string;
  chartData: ReferencedMultiChartDataPoint[];
  datePickerHeaderProps;
  hint?: string;
  groups: string[];
  groupMapping;
  formatValue: (rawValue: ValueType, isRef?: boolean) => string;
  chartFilters;
  thisYearEnabled: boolean;
  lastYearEnabled: boolean;
  summaryData: SummaryPanelItemProps[];
  accountData: TikTokAccountData;
  onSelectSummaryTab: (metricType: string, selected: boolean) => void;
}

/**
 * InternalTikTokSpendScreen component
 */
export const InternalTikTokSpendScreen = ({
  pageTitle,
  chartData,
  datePickerHeaderProps,
  hint = "",
  groups,
  groupMapping,
  formatValue,
  chartFilters,
  thisYearEnabled,
  lastYearEnabled,
  summaryData,
  accountData,
  onSelectSummaryTab,
}: InternalTikTokSpendScreenProps): UIElement => {
  const query = useMediaQuery();
  const theme = useTheme();

  const lines = groups.map((group) => {
    return {
      datumName: group + MetricTypes.TIKTOK_AD_SPEND,
      name: `${getGroupNameWithId(group, accountData)} This Year`,
      referenceName: `${getGroupNameWithId(group, accountData)} Last Year`,
      colorId: groupMapping[group],
    };
  });

  const { onClickDateRes, hideMonthly, hideWeekly, selectedButtonIndex, ...rest } = chartFilters;
  const temporalResPickerProps = { onClickDateRes, hideWeekly, hideMonthly, selectedButtonIndex };
  const chartFilterSwitchesProps = {
    switchOneColor: theme.palette.bg.accent.strong,
    switchTwoColor: theme.palette.bg.accent.strong,
    ...rest,
    switchOneOn: thisYearEnabled,
    switchTwoOn: lastYearEnabled,
  };

  return (
    <FullWidthPage title={pageTitle}>
      <DataGroupContainer>
        <DatePickerHeader
          summary={query("sm").windowGt && hint ? hint : ""}
          onChange={datePickerHeaderProps.onChange}
          currentPreset={datePickerHeaderProps.currentPreset}
          dateRangePresets={datePickerHeaderProps.dateRangePresets}
          startDate={datePickerHeaderProps.startDate}
          endDate={datePickerHeaderProps.endDate}
        />
        <SummaryPanel
          summaryItems={summaryData}
          metricReferenceText="vs. last year"
          loading={false}
          onSelect={onSelectSummaryTab}
          useAnalytics={false}
        />
        <PanelContainer>
          <PanelPadding>
            <ChartTopRow>
              {temporalResPickerProps ? (
                <TemporalResolutionPickers {...temporalResPickerProps} className="no-padding" />
              ) : null}
              {chartFilterSwitchesProps ? <ChartFilterSwitches {...chartFilterSwitchesProps} /> : null}
            </ChartTopRow>
            <ReferencedMultiLineBarChart
              data={chartData}
              lines={lines}
              formatValueBar={formatValue}
              formatValueLine={formatValue}
              showLegend={true}
              currentRangeEnabled={thisYearEnabled}
              referenceRangeEnabled={lastYearEnabled}
            />
          </PanelPadding>
        </PanelContainer>
      </DataGroupContainer>
    </FullWidthPage>
  );
};
